import { ChevronDown, ChevronUp, GripVertical } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { DraggableAttributes } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';

import { IconButton } from '@/components/ui/icon-button';

/**
 * The up / grip / down column beside a reorderable Flow row.
 *
 * Shared by the two ordered lists — navigation events and the sensor rows — because order means the
 * same thing in both (first match wins) and the keyboard fallback is not optional: dnd-kit is wired
 * here with pointer and touch sensors only, so without these buttons a keyboard user could not
 * reorder either list at all.
 */
export function FlowReorderColumn({ index, total, dragLabel, onMoveUp, onMoveDown, upRef, downRef, listeners, attributes }: {
  index: number;
  total: number;
  dragLabel: string;
  onMoveUp: () => void;
  onMoveDown: () => void;
  upRef?: (el: HTMLButtonElement | null) => void;
  downRef?: (el: HTMLButtonElement | null) => void;
  listeners?: SyntheticListenerMap;
  attributes?: DraggableAttributes;
}) {
  const { t } = useTranslation();
  return (
    // Centred on the card by the row's own `items-center`, not pinned to its top. Pinning needed a
    // fixed top margin tuned to a tall card's header, and the column is taller than a short card's
    // whole header row — so on a sensor row, or any collapsed card, the handles sat visibly low.
    // Centring is also what every other reorderable card list in the app does.
    <div className='flex shrink-0 flex-col items-center gap-0.5'>
      <IconButton
        ref={upRef}
        type='button'
        size='sm'
        // Pointer-down rather than click so the drag sensor never swallows the press, and a
        // keyboard activation (detail === 0) is handled separately so it does not fire twice.
        onPointerDown={(e) => { if (e.button !== 0) return; e.stopPropagation(); onMoveUp(); }}
        onClick={(e) => { e.stopPropagation(); if (e.detail === 0) onMoveUp(); }}
        disabled={index === 0}
        aria-label={t('task.moveUp')}
      >
        <ChevronUp className='h-4 w-4' />
      </IconButton>
      <div
        {...listeners}
        {...attributes}
        aria-label={dragLabel}
        className='flex h-6 w-4 cursor-grab items-center justify-center touch-none text-ws-muted hover:text-ws-text active:cursor-grabbing focus-visible:[outline:2px_auto]'
      >
        <GripVertical className='h-4 w-4' />
      </div>
      <IconButton
        ref={downRef}
        type='button'
        size='sm'
        onPointerDown={(e) => { if (e.button !== 0) return; e.stopPropagation(); onMoveDown(); }}
        onClick={(e) => { e.stopPropagation(); if (e.detail === 0) onMoveDown(); }}
        disabled={index === total - 1}
        aria-label={t('task.moveDown')}
      >
        <ChevronDown className='h-4 w-4' />
      </IconButton>
    </div>
  );
}
